import { useCallback, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { AgentSearchDebug, AgentSearchProgress, SmartSearchDebug } from 'services/searchService';
import * as searchService from 'services/searchService';

// A mutation, not a query: each search is a fresh run of the models, and the
// same words searched twice are meant to be run twice.
export const useSmartSearch = (options = {}) => {
  return useMutation(searchService.smartSearch, options);
};

export const useAgentSearch = (options = {}) => {
  return useMutation(searchService.agentSearch, options);
};

// The agent search as it runs: the calls finished so far, then the answer. A
// new search drops what an older one still sends.
export const useAgentSearchStream = () => {
  const [steps, setSteps] = useState<AgentSearchProgress[]>([]);
  const [data, setData] = useState<AgentSearchDebug | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const current = useRef(0);

  const search = useCallback((dto: { input: string; storeId?: string }) => {
    const id = ++current.current;
    setSteps([]);
    setData(null);
    setIsError(false);
    setIsLoading(true);

    searchService
      .agentSearchStream(dto, (event) => {
        if (id !== current.current) return;
        if (event.type === 'step') setSteps((existing) => [...existing, event]);
        else if (event.type === 'done') setData(event);
        else setIsError(true);
      })
      .catch(() => id === current.current && setIsError(true))
      .finally(() => id === current.current && setIsLoading(false));
  }, []);

  return { search, steps, data, isLoading, isError };
};

export interface SmartSearchStep {
  step: string;
  pieces?: string[];
  categories?: string[];
  found?: number;
}

// The search as it runs: the steps finished so far, then the answer. A new
// search drops what an older one still sends.
export const useSmartSearchStream = () => {
  const [steps, setSteps] = useState<SmartSearchStep[]>([]);
  const [data, setData] = useState<SmartSearchDebug | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const current = useRef(0);

  const search = useCallback((dto: { input: string; storeId?: string }) => {
    const id = ++current.current;
    setSteps([]);
    setData(null);
    setIsError(false);
    setIsLoading(true);

    searchService
      .smartSearchStream(dto, (event) => {
        if (id !== current.current) return;
        if (event.type === 'step') setSteps((existing) => [...existing, event]);
        else if (event.type === 'done') setData(event);
        else setIsError(true);
      })
      .catch(() => id === current.current && setIsError(true))
      .finally(() => id === current.current && setIsLoading(false));
  }, []);

  return { search, steps, data, isLoading, isError };
};
